import { apiFetch } from '../config/api';
import { debugLog, debugError } from '../utils/debugLog';

/**
 * Feedback Service
 *
 * Handles all feedback-related API operations for templates and executions.
 * Based on the feedback domain analysis, feedback is tied to executions,
 * which are then linked to templates.
 */
class FeedbackService {
  /**
   * Get feedback for a specific template
   * @param {string} templateId - Template ID
   * @param {Object} options - Query options
   * @param {number} options.page - Page number (default: 1)
   * @param {number} options.pageSize - Page size (default: 50)
   * @param {string} options.sortBy - Sort field (default: 'created_at')
   * @param {string} options.sortOrder - Sort order (default: 'desc')
   * @returns {Promise<Object>} Feedback data with pagination
   */
  async getTemplateFeedback(templateId, options = {}) {
    // Options are passed to API but not used locally
    // eslint-disable-next-line no-unused-vars
    const _unusedOptions = options;

    try {
      // The API endpoint doesn't use query parameters, it's just /template/{template_id}
      const response = await apiFetch(
        `/api/v1/feedback/template/${templateId}`,
        { timeoutMs: 3000 }
      );
      debugLog('🔍 FeedbackService.getTemplateFeedback: Response:', response);
      return response;
    } catch (error) {
      debugError('Failed to fetch template feedback:', error);
      throw error;
    }
  }

  /**
   * Get feedback analytics for a template
   * @param {string} templateId - Template ID
   * @returns {Promise<Object>} Feedback analytics
   */
  async getTemplateFeedbackAnalytics(templateId) {
    try {
      // Try the analytics endpoint first
      const response = await apiFetch(
        `/api/v1/feedback/template/${templateId}/analytics`,
        { timeoutMs: 3000 }
      );
      return response;
    } catch (error) {
      // If analytics endpoint is not available (404), calculate from feedback data
      if (error.status === 404 || error.message.includes('Not Found')) {
        debugLog(
          'Analytics endpoint not available, calculating from feedback data...'
        );
        return this.calculateAnalyticsFromFeedback(templateId);
      }
      debugError('Failed to fetch template feedback analytics:', error);
      throw error;
    }
  }

  /**
   * Calculate analytics from feedback data when analytics endpoint is not available
   * @param {string} templateId - Template ID
   * @returns {Promise<Object>} Calculated analytics
   */
  async calculateAnalyticsFromFeedback(templateId) {
    try {
      const feedbackData = await this.getTemplateFeedback(templateId);
      const feedback = feedbackData.feedback || feedbackData;

      if (!feedback || feedback.length === 0) {
        return {
          average_rating: 0,
          total_feedback: 0,
          rating_distribution: {},
          category_scores: {},
        };
      }

      // Calculate analytics
      const ratings = feedback.map(f => f.rating);
      const average_rating =
        ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length;

      // Rating distribution
      const rating_distribution = {};
      ratings.forEach(rating => {
        rating_distribution[rating] = (rating_distribution[rating] || 0) + 1;
      });

      // Category scores
      const category_scores = {};
      const all_categories = new Set();

      feedback.forEach(f => {
        if (f.categories) {
          f.categories.forEach(category => all_categories.add(category));
        }
      });

      all_categories.forEach(category => {
        const category_ratings = feedback
          .filter(f => f.categories && f.categories.includes(category))
          .map(f => f.rating);

        if (category_ratings.length > 0) {
          category_scores[category] =
            category_ratings.reduce((sum, rating) => sum + rating, 0) /
            category_ratings.length;
        }
      });

      return {
        average_rating: Math.round(average_rating * 100) / 100,
        total_feedback: feedback.length,
        rating_distribution,
        category_scores,
      };
    } catch (error) {
      debugError('Failed to calculate analytics from feedback:', error);
      throw error;
    }
  }

  /**
   * Get feedback summary for a template (quick stats)
   * @param {string} templateId - Template ID
   * @returns {Promise<Object>} Feedback summary
   */
  async getTemplateFeedbackSummary(templateId) {
    try {
      // Try the summary endpoint first
      const response = await apiFetch(
        `/api/v1/feedback/template/${templateId}/summary`,
        { timeoutMs: 3000 }
      );
      return response;
    } catch (error) {
      // If summary endpoint is not available (404), calculate from analytics
      if (error.status === 404 || error.message.includes('Not Found')) {
        debugLog(
          'Summary endpoint not available, calculating from analytics...'
        );
        return this.calculateSummaryFromAnalytics(templateId);
      }
      debugError('Failed to fetch template feedback summary:', error);
      throw error;
    }
  }

  /**
   * Calculate summary from analytics when summary endpoint is not available
   * @param {string} templateId - Template ID
   * @returns {Promise<Object>} Calculated summary
   */
  async calculateSummaryFromAnalytics(templateId) {
    try {
      const analytics = await this.getTemplateFeedbackAnalytics(templateId);

      const total_feedback = analytics.total_feedback;
      const average_rating = analytics.average_rating;

      // Determine quality assessment
      let quality_assessment;
      if (total_feedback === 0) {
        quality_assessment = 'No feedback';
      } else if (average_rating >= 4.5) {
        quality_assessment = 'Excellent';
      } else if (average_rating >= 4.0) {
        quality_assessment = 'Very Good';
      } else if (average_rating >= 3.5) {
        quality_assessment = 'Good';
      } else if (average_rating >= 3.0) {
        quality_assessment = 'Fair';
      } else {
        quality_assessment = 'Needs Improvement';
      }

      return {
        template_id: templateId,
        total_feedback,
        average_rating,
        quality_assessment,
        rating_distribution: analytics.rating_distribution,
        category_scores: analytics.category_scores,
      };
    } catch (error) {
      debugError('Failed to calculate summary from analytics:', error);
      throw error;
    }
  }

  /**
   * Submit feedback for an execution
   * @param {Object} feedbackData - Feedback data
   * @param {string} feedbackData.execution_id - Execution ID
   * @param {number} feedbackData.rating - Rating (1-5)
   * @param {string} feedbackData.feedback_text - Feedback text (optional)
   * @param {Array<string>} feedbackData.categories - Feedback categories (optional)
   * @returns {Promise<Object>} Created feedback
   */
  async submitFeedback(feedbackData) {
    try {
      const response = await apiFetch('/api/v1/feedback/', {
        method: 'POST',
        body: JSON.stringify(feedbackData),
        timeoutMs: 3000,
      });
      return response;
    } catch (error) {
      debugError('Failed to submit feedback:', error);
      throw error;
    }
  }

  /**
   * Get feedback for a specific execution
   * @param {string} executionId - Execution ID
   * @returns {Promise<Object>} Feedback data
   */
  async getExecutionFeedback(executionId) {
    try {
      const response = await apiFetch(
        `/api/v1/feedback/execution/${executionId}`,
        { timeoutMs: 3000 }
      );
      return response;
    } catch (error) {
      debugError('Failed to fetch execution feedback:', error);
      throw error;
    }
  }

  /**
   * Get all feedback with pagination
   * @param {Object} options - Query options
   * @param {number} options.page - Page number (default: 1)
   * @param {number} options.pageSize - Page size (default: 50)
   * @param {string} options.sortBy - Sort field (default: 'created_at')
   * @param {string} options.sortOrder - Sort order (default: 'desc')
   * @returns {Promise<Object>} Feedback data with pagination
   */
  async getAllFeedback(options = {}) {
    const {
      page = 1,
      pageSize = 50,
      sortBy = 'created_at',
      sortOrder = 'desc',
    } = options;

    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
      sort_by: sortBy,
      sort_order: sortOrder,
    });

    try {
      const response = await apiFetch(
        `/api/v1/feedback/?${params.toString()}`,
        {
          timeoutMs: 3000,
        }
      );
      return response;
    } catch (error) {
      debugError('Failed to fetch all feedback:', error);
      throw error;
    }
  }

  /**
   * Get feedback health status
   * @returns {Promise<Object>} Health status
   */
  async getFeedbackHealth() {
    try {
      const response = await apiFetch('/api/v1/feedback/health', {
        timeoutMs: 2500,
      });
      return response;
    } catch (error) {
      debugError('Failed to fetch feedback health:', error);
      throw error;
    }
  }
}

// Create singleton instance
const feedbackService = new FeedbackService();

export default feedbackService;
